import { useEffect, useRef } from "react";

import { queryClient } from "@/lib/api/queryClient";
import {
  AI_DATA_MUTATED_EVENT,
  cascadesToRoleTemplates,
  queryPrefixesForDomains,
  resolveSyncDomains,
  type AiDataMutatedDetail,
} from "@/lib/ai-sync/ai-sync";
import { isRoleTemplateViewKey } from "@/lib/authz/invalidateRoleTemplateViews";

/**
 * Central react-query live-sync for AI ("Velo") server-side mutations.
 *
 * Velo's tools run server-side, so when one mutates data the browser never sees
 * the write and query-backed pages stay stale until reload. AiAssistantProvider
 * fires {@link AI_DATA_MUTATED_EVENT} on each successful server tool; this bridge
 * resolves which react-query key prefixes that tool affects (see
 * {@link queryPrefixesForTool}) and invalidates the matching queries — for EVERY
 * query-backed page at once (dashboard, email, devices, workflows, sender,
 * requests, attachments, additional fields, documents…), open or backgrounded,
 * with zero per-page code.
 *
 * Scope: every event query key is `[prefix, eventId, …]`, so the predicate
 * matches `queryKey[0]` against the affected prefixes AND `queryKey[1]` against
 * the mutated event — a mutation in event A never refetches event B.
 *
 * B2: a field-definition tool also marks the event's role-template reads stale
 * (the Roles list, every single template, the preview-bar editor's read) —
 * matched by shape, see {@link cascadesToRoleTemplates}.
 *
 * Custom stores (seating, slotting) are intentionally NOT covered here — their
 * loader queries would re-`initialize()` and reset UI state. They subscribe via
 * `useAiDataSync` / are already live over the slotting WebSocket.
 *
 * Mounted ONCE at the app root (main.tsx, under QueryClientProvider), next to
 * AiBackgroundJobBridge.
 */
type PendingSync = {
  /** react-query key prefixes (`queryKey[0]`) to invalidate. */
  prefixes: Set<string>;
  /** B2: also the event's role-template reads ({@link isRoleTemplateViewKey}). */
  roleTemplates: boolean;
};

export function AiDataSyncBridge(): null {
  // Coalesce a burst of tool_results (e.g. a bulk furniture create) into one
  // invalidation pass per event: accumulate affected prefixes, flush on a short
  // debounce.
  const pendingRef = useRef<Map<string, PendingSync>>(new Map()); // eventId → what to refresh
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const flush = () => {
      timerRef.current = null;
      const batches = pendingRef.current;
      pendingRef.current = new Map();
      for (const [eventId, { prefixes, roleTemplates }] of batches) {
        if (prefixes.size === 0 && !roleTemplates) continue;
        queryClient.invalidateQueries({
          predicate: (q) => {
            const key = q.queryKey;
            if (!Array.isArray(key) || key.length < 2) return false;
            // Every event-scoped key is `[prefix, eventId, …]`. Require a string
            // at [1] so a non-event-scoped key that happens to share a prefix
            // (e.g. a company-level variant) can never be matched.
            if (typeof key[1] !== "string") return false;
            // eventId === "" is the sentinel for an unknown scope → match any.
            if (eventId !== "" && key[1] !== eventId) return false;
            if (roleTemplates && isRoleTemplateViewKey(key)) return true;
            const prefix = key[0];
            return typeof prefix === "string" && prefixes.has(prefix);
          },
        });
      }
    };

    const onMutated = (e: Event) => {
      const detail = (e as CustomEvent<AiDataMutatedDetail>).detail;
      if (!detail?.toolName) return;
      const domains = resolveSyncDomains(detail.toolName);
      const prefixes = queryPrefixesForDomains(domains);
      const roleTemplates = cascadesToRoleTemplates(domains);
      // A tool that affects only custom-store domains has nothing to invalidate.
      if (prefixes.size === 0 && !roleTemplates) return;
      const eventKey = detail.eventId ?? "";
      const bucket = pendingRef.current.get(eventKey) ?? { prefixes: new Set<string>(), roleTemplates: false };
      for (const p of prefixes) bucket.prefixes.add(p);
      bucket.roleTemplates ||= roleTemplates;
      pendingRef.current.set(eventKey, bucket);

      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(flush, 300);
    };

    window.addEventListener(AI_DATA_MUTATED_EVENT, onMutated);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      window.removeEventListener(AI_DATA_MUTATED_EVENT, onMutated);
    };
  }, []);

  return null;
}
